import { useState, useEffect } from 'react';
import {
  InformationCircleIcon,
  ArrowPathIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';

function columnIndexToLetter(index) {
  let letter = '';
  let i = index;
  while (i >= 0) {
    letter = String.fromCharCode((i % 26) + 65) + letter;
    i = Math.floor(i / 26) - 1;
  }
  return letter;
}

function letterToColumnIndex(letter) {
  let index = 0;
  for (let i = 0; i < letter.length; i++) {
    index = index * 26 + (letter.charCodeAt(i) - 64);
  }
  return index - 1;
}

/**
 * Controls for adjusting the data range (header row, row bounds, column bounds).
 *
 * Props:
 * - detectedHeaderRow: 0-indexed row the backend detected as the header
 * - totalRows: total number of raw rows
 * - totalColumns: total number of raw columns
 * - headerRow: current 0-indexed header row
 * - dataRange: { startRow, endRow, startCol, endCol } (0-indexed)
 * - onHeaderRowChange: (newVal: number) => void
 * - onDataRangeChange: (newRange: object) => void
 * - onApply: () => void  — called when user confirms changes
 * - isApplying: boolean
 * - accentColor: 'primary' | 'emerald'
 * - csvInfo: { detectedDelimiter, detectedEncoding } | null — shown for CSV files
 * - onDelimiterChange: (delimiter) => void | null
 * - onEncodingChange: (encoding) => void | null
 */
export default function DataRangeSelector({
  detectedHeaderRow = 0,
  totalRows = 0,
  totalColumns = 0,
  headerRow = 0,
  dataRange = {},
  onHeaderRowChange,
  onDataRangeChange,
  onApply,
  isApplying = false,
  accentColor = 'primary',
  csvInfo = null,
  onDelimiterChange = null,
  onEncodingChange = null
}) {
  const [expanded, setExpanded] = useState(false);

  const { startRow = 0, endRow, startCol = 0, endCol } = dataRange;

  const hasCustomRange = headerRow !== detectedHeaderRow ||
    startRow !== (detectedHeaderRow + 1) ||
    endRow != null ||
    startCol !== 0 ||
    endCol != null;

  // Auto-expand if the detected header isn't row 0 (indicates potential issues)
  useEffect(() => {
    if (detectedHeaderRow > 0) setExpanded(true);
  }, [detectedHeaderRow]);

  const delimiterLabels = { ',': 'Comma', '\t': 'Tab', ';': 'Semicolon', '|': 'Pipe' };

  const btnAccent = accentColor === 'emerald'
    ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
    : 'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20';

  const badgeAccent = accentColor === 'emerald'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';

  return (
    <div className="card p-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AdjustmentsHorizontalIcon className="w-5 h-5 text-surface-400" />
          <h3 className="font-medium text-surface-900 dark:text-white text-sm">
            Data Range
          </h3>
          {detectedHeaderRow > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${badgeAccent}`}>
              Header detected at row {detectedHeaderRow + 1}
            </span>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`text-sm px-3 py-1 rounded-md transition-colors ${btnAccent}`}
        >
          {expanded ? 'Hide' : 'Adjust Range'}
        </button>
      </div>

      {/* Info banner */}
      {detectedHeaderRow > 0 && !expanded && (
        <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-xs">
          <InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-blue-700 dark:text-blue-300">
            We detected your data starts at row {detectedHeaderRow + 1}. Click "Adjust Range" if this looks wrong.
          </p>
        </div>
      )}

      {/* Expanded controls */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Row controls */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                Header Row
              </label>
              <input
                type="number"
                min={1}
                max={totalRows}
                value={headerRow + 1}
                onChange={(e) => {
                  const val = Math.max(0, parseInt(e.target.value || '1', 10) - 1);
                  onHeaderRowChange(val);
                  onDataRangeChange({ ...dataRange, startRow: val + 1 });
                }}
                className="input py-1.5 text-sm w-full"
              />
              <p className="text-[10px] text-surface-400 mt-0.5">1-indexed row number</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                Data Start Row
              </label>
              <input
                type="number"
                min={1}
                max={totalRows}
                value={(startRow || 0) + 1}
                onChange={(e) => {
                  const val = Math.max(0, parseInt(e.target.value || '1', 10) - 1);
                  onDataRangeChange({ ...dataRange, startRow: val });
                }}
                className="input py-1.5 text-sm w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                Data End Row <span className="text-surface-400">(optional)</span>
              </label>
              <input
                type="number"
                min={1}
                max={totalRows}
                value={endRow != null ? endRow + 1 : ''}
                placeholder="All"
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    onDataRangeChange({ ...dataRange, endRow: undefined });
                  } else {
                    const val = Math.max(0, parseInt(raw, 10) - 1);
                    onDataRangeChange({ ...dataRange, endRow: val });
                  }
                }}
                className="input py-1.5 text-sm w-full"
              />
            </div>
          </div>

          {/* Column controls */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                Start Column
              </label>
              <input
                type="text"
                value={columnIndexToLetter(startCol)}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                  if (val) {
                    onDataRangeChange({ ...dataRange, startCol: letterToColumnIndex(val) });
                  }
                }}
                className="input py-1.5 text-sm w-full uppercase"
                placeholder="A"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                End Column <span className="text-surface-400">(optional)</span>
              </label>
              <input
                type="text"
                value={endCol != null ? columnIndexToLetter(endCol) : ''}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                  if (!val) {
                    onDataRangeChange({ ...dataRange, endCol: undefined });
                  } else {
                    onDataRangeChange({ ...dataRange, endCol: letterToColumnIndex(val) });
                  }
                }}
                className="input py-1.5 text-sm w-full uppercase"
                placeholder="All"
              />
            </div>
          </div>

          {/* CSV-specific controls */}
          {csvInfo && (
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-surface-200 dark:border-surface-700">
              <div>
                <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                  Delimiter
                </label>
                <select
                  value={csvInfo.detectedDelimiter || ','}
                  onChange={(e) => onDelimiterChange?.(e.target.value)}
                  className="input py-1.5 text-sm w-full"
                >
                  {Object.entries(delimiterLabels).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">
                  Encoding
                </label>
                <select
                  value={csvInfo.detectedEncoding || 'utf-8'}
                  onChange={(e) => onEncodingChange?.(e.target.value)}
                  className="input py-1.5 text-sm w-full"
                >
                  <option value="utf-8">UTF-8</option>
                  <option value="latin1">Latin-1 (ISO-8859-1)</option>
                  <option value="windows-1252">Windows-1252</option>
                </select>
              </div>
            </div>
          )}

          {/* Apply button */}
          <div className="flex items-center justify-between pt-2">
            {hasCustomRange && (
              <button
                onClick={() => {
                  onHeaderRowChange(detectedHeaderRow);
                  onDataRangeChange({
                    startRow: detectedHeaderRow + 1,
                    endRow: undefined,
                    startCol: 0,
                    endCol: undefined
                  });
                }}
                className="text-xs text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
              >
                Reset to auto-detected
              </button>
            )}
            <button
              onClick={onApply}
              disabled={isApplying}
              className={`btn-primary text-sm px-4 py-1.5 ml-auto ${accentColor === 'emerald' ? '!bg-emerald-600 hover:!bg-emerald-700' : ''}`}
            >
              {isApplying ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-1.5" />
                  Applying...
                </>
              ) : (
                'Apply Range'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
